import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import CoachBannerMascot from '../../assets/coach_banner_mascot.png';

const CHESS_QUOTES = [
    { text: "Every chess master was once a beginner.", author: "Irving Chernev" },
    { text: "The pawns are the soul of chess.", author: "François-André Danican Philidor" },
    { text: "Tactics is what you do when there is something to do; strategy is what you do when there is nothing to do.", author: "Savielly Tartakower" },
    { text: "Chess is the struggle against the error.", author: "Johannes Zukertort" },
    { text: "In chess, as in life, a man can be his own greatest enemy.", author: "Alexander Alekhine" },
    { text: "Without error there can be no brilliancy.", author: "Emanuel Lasker" },
    { text: "Help your pieces so they can help you.", author: "Paul Morphy" }
];

export default function CoachDashboardHome({ 
    user, 
    myProfile, 
    myBatches, 
    allStudents, 
    allAcademyStudents = [], 
    classesList, 
    changeStatus,
    activeClass,
    setActiveTab,
    setDashTab
}) {
    const [myHomeworks, setMyHomeworks] = useState([]);
    const [selectedXrayHw, setSelectedXrayHw] = useState(null);
    const [commentInput, setCommentInput] = useState({});
    
    // Coach daily checklist state (saved in localStorage)
    const [checklist, setChecklist] = useState(() => {
        const saved = localStorage.getItem(`coach_checklist_${user?.email}`);
        return saved ? JSON.parse(saved) : {
            meet: false,
            homework: false,
            curriculum: false,
            leaderboard: false
        };
    });

    useEffect(() => {
        localStorage.setItem(`coach_checklist_${user?.email}`, JSON.stringify(checklist));
    }, [checklist, user?.email]);

    // Choose a random quote once on mount
    const randomQuote = useMemo(() => {
        const index = Math.floor(Math.random() * CHESS_QUOTES.length);
        return CHESS_QUOTES[index];
    }, []);

    // Homework Scan Logs subscription
    useEffect(() => {
        if (!user?.email) return;
        const qHW = query(collection(db, 'homeworks'), where('assignedBy', '==', user.email.toLowerCase()));
        const unsub = onSnapshot(qHW, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setMyHomeworks(list);
        });
        return () => unsub();
    }, [user]);

    const handleSaveComment = async (hwId, studentEmail) => {
        const comment = commentInput[studentEmail];
        if (!comment) return toast.error("Write a comment first!");
        const loadingId = toast.loading("Sending Feedback...");
        try {
            const safeEmail = studentEmail.replace(/\./g, '_');
            await updateDoc(doc(db, "homeworks", hwId), { [`coachComments.${safeEmail}`]: comment });
            toast.success("Feedback Sent! 💌", { id: loadingId });
        } catch(e) { toast.error("Failed to send.", { id: loadingId }); }
    };

    // Calculate dynamic stats
    const totalStudents = allStudents.length;
    const totalBatches = myBatches.length;
    
    const upcomingTodayCount = useMemo(() => {
        const todayStr = new Date().toDateString();
        return classesList.filter(c => {
            if (c.status !== 'UPCOMING') return false;
            return new Date(c.scheduledAt).toDateString() === todayStr;
        }).length;
    }, [classesList]);

    const pendingHwReviews = useMemo(() => {
        return myHomeworks.filter(hw => {
            const submissions = hw.activityLogs?.length || 0;
            if (submissions === 0) return false;
            const commentedCount = Object.keys(hw.coachComments || {}).length;
            return submissions > commentedCount;
        }).length;
    }, [myHomeworks]);

    const copyMeetLink = () => {
        const link = myProfile?.meetLink || "https://meet.google.com";
        navigator.clipboard.writeText(link);
        toast.success("Video Call Link copied to clipboard! 📋");
    };

    const topStudent = useMemo(() => {
        if (allStudents.length === 0) return null;
        return [...allStudents].sort((a, b) => (b.elo || 1200) - (a.elo || 1200))[0];
    }, [allStudents]);

    const upcomingTodayList = useMemo(() => {
        const todayStr = new Date().toDateString();
        return classesList.filter(c => {
            if (c.status !== 'UPCOMING') return false;
            return new Date(c.scheduledAt).toDateString() === todayStr;
        }).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    }, [classesList]);

    // Student ELO Growth Calculation (Real Aggregated Metrics)
    const avgStudentElo = useMemo(() => {
        if (allStudents.length === 0) return 1200;
        const total = allStudents.reduce((sum, s) => sum + (s.elo || 1200), 0);
        return Math.round(total / allStudents.length);
    }, [allStudents]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '35px', paddingBottom: '30px' }}>
            
            {/* Styles Injection */}
            <style>{`
                .coach-quest-banner {
                    display: flex;
                    align-items: stretch;
                    justify-content: space-between;
                    min-height: 275px;
                    position: relative;
                    overflow: visible;
                    width: 100%;
                }
                .coach-quest-banner-left {
                    padding: 35px 45px;
                    flex: 1 1 500px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    position: relative;
                    z-index: 5;
                }
                .coach-quest-banner-right {
                    position: relative;
                    flex: 1 1 280px;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    padding-right: 60px;
                    min-height: 200px;
                    z-index: 5;
                }
                .coach-quest-banner-mascot {
                    height: 310px;
                    width: auto;
                    position: absolute;
                    left: -35px;
                    bottom: -22px;
                    filter: drop-shadow(0 15px 25px rgba(0, 0, 0, 0.35));
                    animation: float-cute 3s ease-in-out infinite;
                    pointer-events: none;
                    z-index: 10;
                }
                .coach-quest-banner-arrow-btn {
                    width: 75px;
                    height: 50px;
                    border-radius: 25px;
                    background: #ffffff;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                    z-index: 6;
                    outline: none;
                    transition: transform 0.2s ease;
                }
                .coach-quest-banner-arrow-btn:hover {
                    transform: scale(1.08);
                }
                .coach-quest-banner-arrow-btn:active {
                    transform: scale(0.95);
                }
                
                /* Pure Claymorphic Cards */
                .clay-card {
                    background: rgba(255, 255, 255, 0.75);
                    border: 3px solid #ffffff;
                    border-radius: 28px;
                    box-shadow: var(--panel-shadow);
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .clay-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--panel-shadow-hover);
                    background: rgba(255, 255, 255, 0.85);
                }
                
                /* Claymorphic Buttons */
                .clay-btn {
                    border-radius: 50px;
                    border: 3px solid #ffffff;
                    box-shadow: var(--neu-shadow);
                    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    cursor: pointer;
                    outline: none;
                }
                .clay-btn:hover {
                    transform: scale(1.04) translateY(-2px);
                    box-shadow: var(--neu-shadow-hover);
                }
                .clay-btn:active {
                    transform: scale(0.96) translateY(1px);
                    box-shadow: var(--neu-shadow-inset);
                }
                
                /* Custom Launchpad Card Styling (No generic solid bottom line) */
                .launchpad-card {
                    padding: 28px 20px;
                    cursor: pointer;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                }
                .launchpad-card:hover {
                    box-shadow: var(--panel-shadow-hover);
                }
                .launchpad-card:hover .icon-bubble {
                    transform: scale(1.15) rotate(5deg);
                }
                .icon-bubble {
                    width: 70px;
                    height: 70px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    border: 2px solid #ffffff;
                    box-shadow: var(--neu-shadow);
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                
                @media (max-width: 768px) {
                    .coach-quest-banner {
                        flex-direction: column;
                        min-height: auto;
                        padding-bottom: 25px;
                    }
                    .coach-quest-banner-left {
                        padding: 30px 25px;
                        flex: none;
                        align-items: center;
                        text-align: center;
                    }
                    .coach-quest-banner-right {
                        flex: none;
                        justify-content: center;
                        padding-right: 0;
                        min-height: 180px;
                        margin-top: 10px;
                    }
                    .coach-quest-banner-mascot {
                        position: relative;
                        left: auto;
                        bottom: 0;
                        height: 180px;
                        z-index: 10;
                    }
                    .coach-quest-banner-arrow-btn {
                        display: none;
                    }
                }
            `}</style>

            {/* 🔴 LIVE CLASS TELEPORTATION BANNER */}
            {activeClass && (
                <div 
                    onClick={() => setActiveTab('classroom')}
                    className="animate-pulse-ring animate-wiggle" 
                    style={{ 
                        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', 
                        color: 'white', 
                        padding: '20px 28px', 
                        borderRadius: '28px', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        boxShadow: '0 15px 35px rgba(239, 68, 68, 0.3)',
                        border: '3px solid rgba(255, 255, 255, 0.95)',
                        flexWrap: 'wrap',
                        gap: '15px'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span style={{ fontSize: '28px', animation: 'float-cute 3s ease-in-out infinite' }}>📢</span>
                        <div>
                            <div style={{ fontWeight: '900', fontSize: '18px', letterSpacing: '0.5px' }}>CLASSROOM SESSION IS LIVE!</div>
                            <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '3px', fontWeight: 'bold' }}>
                                Batch <strong>{activeClass.batchTitle || 'Custom'}</strong> (Topic: <em>{activeClass.topic}</em>) is currently in session.
                            </div>
                        </div>
                    </div>
                    <button 
                        className="clay-btn"
                        style={{ 
                            padding: '10px 22px', 
                            background: 'white', 
                            color: '#b91c1c', 
                            fontWeight: '900', 
                            fontSize: '13px', 
                        }}
                    >
                        🚀 TELEPORT NOW
                    </button>
                </div>
            )}

            {/* 🌟 PREMIUM MASCOT HERO BANNER CARD (Exactly like Student Banner) */}
            <div className="coach-quest-banner">
                {/* 🌊 Backdrop Card with Gradient & Bottom Wave Curves */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #8b5cf6 100%)',
                    border: '3px solid rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 15px 35px rgba(109, 40, 217, 0.3)',
                    borderRadius: '28px',
                    overflow: 'hidden',
                    zIndex: 1,
                    pointerEvents: 'none'
                }}>
                    <svg viewBox="0 0 1440 200" style={{ position: 'absolute', bottom: -1, left: 0, width: '100%', height: '60px', pointerEvents: 'none', zIndex: 1 }} preserveAspectRatio="none">
                        <path fill="var(--bg-primary)" d="M0,120 C180,180 320,60 480,120 C640,180 800,100 960,130 C1120,160 1280,110 1440,120 L1440,200 L0,200 Z" />
                        <path fill="rgba(255,255,255,0.15)" d="M0,90 C120,130 240,60 360,100 C480,140 600,80 720,110 C840,140 960,90 1080,110 C1200,130 1320,100 1440,110 L1440,200 L0,200 Z" />
                    </svg>
                </div>

                {/* Banner Content Left */}
                <div className="coach-quest-banner-left">
                    <span style={{ 
                        fontSize: '11px', 
                        fontWeight: '900', 
                        textTransform: 'uppercase', 
                        color: '#ffffff', 
                        letterSpacing: '2px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        padding: '5px 12px',
                        borderRadius: '30px',
                        border: '1px solid rgba(255, 255, 255, 0.35)',
                        display: 'inline-block',
                        marginBottom: '15px',
                        width: 'fit-content'
                    }}>
                        Academy Instructor HQ 🎓
                    </span>
                    <h1 style={{ 
                        fontSize: '38px', 
                        fontWeight: '900', 
                        margin: '0 0 10px 0', 
                        color: '#ffffff', 
                        lineHeight: '1.2',
                        textShadow: '3px 4px 0px #4c1d95, 0 10px 20px rgba(0,0,0,0.25)',
                        letterSpacing: '-0.5px'
                    }}>
                        Inspire the Next Generation
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px', margin: '0 0 25px 0', lineHeight: '1.6', fontWeight: '600', maxWidth: '520px' }}>
                        "{randomQuote.text}" — <strong style={{ color: '#f59e0b' }}>{randomQuote.author}</strong>
                    </p>

                    <div>
                        <button 
                            onClick={() => setActiveTab('classroom')}
                            className="clay-btn"
                            style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '12px', 
                                padding: '14px 32px', 
                                fontSize: '15px', 
                                fontWeight: '900',
                                background: '#ffffff',
                                color: '#6d28d9',
                                border: 'none',
                                zIndex: 10,
                                position: 'relative'
                            }}
                        >
                            🏫 OPEN TEACHING WORKSPACE
                            <span style={{ 
                                display: 'inline-flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                width: '26px', 
                                height: '26px', 
                                background: '#6d28d9', 
                                color: '#ffffff', 
                                borderRadius: '50%', 
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}>
                                ➔
                            </span>
                        </button>
                    </div>
                </div>

                {/* Banner Content Right */}
                <div className="coach-quest-banner-right">
                    {/* Waving Mascot Overlay (Breaks Card borders exactly like Student banner) */}
                    <img 
                        src={CoachBannerMascot} 
                        alt="Coach Mascot" 
                        className="coach-quest-banner-mascot"
                    />

                    {/* White Pill Button with Arrow */}
                    <button 
                        onClick={() => setActiveTab('classroom')}
                        className="coach-quest-banner-arrow-btn"
                        title="Enter Classroom"
                    >
                        <span style={{ fontSize: '24px', color: '#6d28d9', fontWeight: 'bold' }}>➔</span>
                    </button>
                </div>
            </div>

            {/* 🧸 METRIC STATS ROW (Neumorphic Clay style) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
                <div className="clay-card" style={{ padding: '20px 15px', textAlign: 'center' }}>
                    <span style={{ fontSize: '28px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>👥</span>
                    <div style={{ fontSize: '24px', fontWeight: '950', color: '#2563eb', margin: '6px 0 2px 0' }}>{totalStudents}</div>
                    <div style={{ fontSize: '11px', fontWeight: '850', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Students Coached</div>
                </div>
                <div className="clay-card" style={{ padding: '20px 15px', textAlign: 'center' }}>
                    <span style={{ fontSize: '28px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>🏫</span>
                    <div style={{ fontSize: '24px', fontWeight: '950', color: '#10b981', margin: '6px 0 2px 0' }}>{totalBatches}</div>
                    <div style={{ fontSize: '11px', fontWeight: '850', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Batches</div>
                </div>
                <div className="clay-card" style={{ padding: '20px 15px', textAlign: 'center' }}>
                    <span style={{ fontSize: '28px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>🗓️</span>
                    <div style={{ fontSize: '24px', fontWeight: '950', color: '#f59e0b', margin: '6px 0 2px 0' }}>{upcomingTodayCount}</div>
                    <div style={{ fontSize: '11px', fontWeight: '850', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Today's Classes</div>
                </div>
                <div className="clay-card" style={{ padding: '20px 15px', textAlign: 'center' }}>
                    <span style={{ fontSize: '28px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>📝</span>
                    <div style={{ fontSize: '24px', fontWeight: '950', color: '#a855f7', margin: '6px 0 2px 0' }}>{pendingHwReviews}</div>
                    <div style={{ fontSize: '11px', fontWeight: '850', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reviews Waiting</div>
                </div>
            </div>

            {/* ⚡ CLAYMORPHIC LAUNCHPAD (No solid bottom generic line) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>⚡</span> Interactive Launchpad
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
                    <div 
                        onClick={() => setActiveTab('classroom')} 
                        className="clay-card launchpad-card"
                    >
                        <div className="icon-bubble" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#2563eb' }}>🏫</div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>Go to Classroom</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Schedule classes, manage batches, or start live.</span>
                    </div>

                    <div 
                        onClick={() => setActiveTab('homework')} 
                        className="clay-card launchpad-card"
                    >
                        <div className="icon-bubble" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>📝</div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>Homework Manager</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Select coordinates & assign training puzzles.</span>
                    </div>

                    <div 
                        onClick={() => setDashTab('curriculum')} 
                        className="clay-card launchpad-card"
                    >
                        <div className="icon-bubble" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>📚</div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>Curriculum Library</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Browse opening lessons & interactive books.</span>
                    </div>

                    <div 
                        onClick={() => setActiveTab('board-tools')} 
                        className="clay-card launchpad-card"
                    >
                        <div className="icon-bubble" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>🛠️</div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>Open Board Tools</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Setup FENs, practice coordinate positions.</span>
                    </div>

                    <div 
                        onClick={() => setActiveTab('arena')} 
                        className="clay-card launchpad-card"
                    >
                        <div className="icon-bubble" style={{ background: 'rgba(236, 72, 153, 0.15)', color: '#ec4899' }}>⚔️</div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>Matchmaking Arena</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Spectate ongoing student battles or tournaments.</span>
                    </div>
                </div>
            </div>

            {/* THREE-COLUMN CLAY GRID: TIMELINE, NOTICEBOARD & DAILY CHECKLIST */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
                
                {/* Column 1: Today's Class Timeline */}
                <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)' }}>🗓️ Today's Timeline</h4>
                        <span style={{ fontSize: '11px', fontWeight: '900', color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: '4px 10px', borderRadius: '20px', border: '1.5px solid rgba(37, 99, 235, 0.2)' }}>
                            {upcomingTodayList.length} Classes
                        </span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '280px', overflowY: 'auto', paddingRight: '5px' }}>
                        {upcomingTodayList.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px', fontWeight: 'bold' }}>
                                No classes scheduled for today. Relax, study chess, or review students! ☕♟️
                            </div>
                        ) : (
                            upcomingTodayList.map(c => (
                                <div key={c.id} style={{ padding: '15px', background: 'rgba(255, 255, 255, 0.45)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.8)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <strong style={{ fontSize: '14px', color: 'var(--text-primary)', display: 'block' }}>{c.topic}</strong>
                                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>🏫 {c.batchTitle || 'Custom Batch'}</span>
                                        </div>
                                        <span style={{ fontSize: '11px', fontWeight: '900', color: '#2563eb' }}>
                                            🕒 {new Date(c.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            if (c.status === 'UPCOMING') changeStatus(c.id, 'LIVE');
                                            setActiveTab('classroom');
                                        }}
                                        className="clay-btn"
                                        style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            background: '#10b981', 
                                            color: 'black', 
                                            fontWeight: 'bold', 
                                            fontSize: '12px', 
                                            border: 'none'
                                        }}
                                    >
                                        ▶ Start Teaching Room
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Column 2: Academy Announcements (ERP Vibe) */}
                <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)' }}>📢 Noticeboard</h4>
                        <span style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '4px 10px', borderRadius: '20px' }}>
                            Live Updates
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', maxHeight: '280px' }}>
                        <div style={{ padding: '12px', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '14px', borderLeft: '4px solid #2563eb' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>🏆 Inter-Academy Chess League</div>
                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 0 0', lineHeight: 1.4 }}>
                                Saturday registers are now open. Coordinate ELO ranks and assign student rosters by Friday evening.
                            </p>
                        </div>

                        <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '14px', borderLeft: '4px solid #10b981' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>📚 Endgames Syllabus Updated</div>
                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 0 0', lineHeight: 1.4 }}>
                                12 new King-and-Pawn endgame interactive chapters have been loaded into the Curriculum Library.
                            </p>
                        </div>

                        <div style={{ padding: '12px', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '14px', borderLeft: '4px solid #f59e0b' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)' }}>🗓️ Database Maintenance</div>
                            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 0 0', lineHeight: 1.4 }}>
                                System backups are scheduled for Sunday at 2 AM EST. Game databases will be offline for 15 minutes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Column 3: Coach Daily Checklist */}
                <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)' }}>📋 Daily Routine Checklist</h4>
                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Saved</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                            <input 
                                type="checkbox" 
                                checked={checklist.meet} 
                                onChange={(e) => setChecklist({...checklist, meet: e.target.checked})} 
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            🎥 Synchronize Live Jitsi room link
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                            <input 
                                type="checkbox" 
                                checked={checklist.homework} 
                                onChange={(e) => setChecklist({...checklist, homework: e.target.checked})} 
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            📝 Review homework X-Ray logs
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                            <input 
                                type="checkbox" 
                                checked={checklist.curriculum} 
                                onChange={(e) => setChecklist({...checklist, curriculum: e.target.checked})} 
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            📚 Verify batch study syllabus notes
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                            <input 
                                type="checkbox" 
                                checked={checklist.leaderboard} 
                                onChange={(e) => setChecklist({...checklist, leaderboard: e.target.checked})} 
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            🏆 Check ELO leaderboard growth
                        </label>
                    </div>
                    
                    {/* Clear Button */}
                    <button 
                        onClick={() => setChecklist({ meet: false, homework: false, curriculum: false, leaderboard: false })}
                        className="clay-btn"
                        style={{ padding: '6px 12px', background: 'transparent', color: 'var(--text-secondary)', fontSize: '11px', border: '1px solid rgba(163, 177, 198, 0.4)', marginTop: '5px' }}
                    >
                        🔄 Reset Routine
                    </button>
                </div>
            </div>

            {/* HIGH CLASS DATA ANALYTICS GRID & JITSI WIDGET */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
                
                {/* Left: Elite Performance Analytics */}
                <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)' }}>📈 Student Progress Analytics (Avg)</h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                        <div style={{ padding: '15px', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '16px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                            <span style={{ fontSize: '10px', fontWeight: '900', color: '#2563eb', textTransform: 'uppercase' }}>Avg Student ELO</span>
                            <div style={{ fontSize: '20px', fontWeight: '950', color: 'var(--text-primary)', marginTop: '4px' }}>♟️ {avgStudentElo}</div>
                        </div>

                        <div style={{ padding: '15px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '16px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                            <span style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', textTransform: 'uppercase' }}>Homework Solved</span>
                            <div style={{ fontSize: '20px', fontWeight: '950', color: 'var(--text-primary)', marginTop: '4px' }}>🎯 84% Rate</div>
                        </div>

                        <div style={{ padding: '15px', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '16px', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
                            <span style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', textTransform: 'uppercase' }}>Active Training Time</span>
                            <div style={{ fontSize: '20px', fontWeight: '950', color: 'var(--text-primary)', marginTop: '4px' }}>⏱️ 185 hrs</div>
                        </div>

                        <div style={{ padding: '15px', background: 'rgba(168, 85, 247, 0.05)', borderRadius: '16px', border: '1px solid rgba(168, 85, 247, 0.1)' }}>
                            <span style={{ fontSize: '10px', fontWeight: '900', color: '#a855f7', textTransform: 'uppercase' }}>Attendance Rate</span>
                            <div style={{ fontSize: '20px', fontWeight: '950', color: 'var(--text-primary)', marginTop: '4px' }}>🏫 96.5%</div>
                        </div>
                    </div>
                </div>

                {/* Right: Personal Jitsi Room Widget & Student Spotlight */}
                <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', justifyStyle: 'stretch', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)' }}>🎥 Jitsi Teleconference Room</h4>
                        <button 
                            onClick={copyMeetLink}
                            className="clay-btn"
                            style={{ padding: '5px 12px', background: 'transparent', fontSize: '11px', fontWeight: 'bold' }}
                        >
                            📋 Copy Link
                        </button>
                    </div>

                    <div style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '16px', border: '1.5px solid rgba(255, 255, 255, 0.65)', boxShadow: 'var(--neu-shadow-inset)', wordBreak: 'break-all' }}>
                        <span style={{ fontSize: '9px', fontWeight: '900', color: 'var(--text-secondary)', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>Meeting Room Link</span>
                        <strong style={{ fontSize: '13px', color: '#2563eb' }}>{myProfile?.meetLink || 'Not configured in profile settings'}</strong>
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <div style={{ 
                                fontSize: '24px', 
                                width: '45px', 
                                height: '45px', 
                                background: 'linear-gradient(135deg, #fef3c7, #fde68a)', 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                borderRadius: '12px', 
                                boxShadow: 'inset 2.5px 2.5px 5px rgba(255, 255, 255, 0.45), inset -2.5px -2.5px 5px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(245, 158, 11, 0.15)', 
                                border: '1.5px solid #fff',
                                flexShrink: 0
                            }}>
                                {topStudent?.avatar || '👤'}
                            </div>
                            <div>
                                <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', display: 'block' }}>Top Student</span>
                                <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{topStudent?.name || 'Anonymous'}</strong>
                            </div>
                        </div>

                        <button 
                            onClick={() => window.open(myProfile?.meetLink || "https://meet.jit.si", "_blank")}
                            className="clay-btn"
                            style={{ 
                                padding: '10px 20px', 
                                background: '#3b82f6', 
                                color: 'white', 
                                fontWeight: 'bold', 
                                fontSize: '13px',
                                border: 'none'
                            }}
                        >
                            🚀 Open Room
                        </button>
                    </div>
                </div>
            </div>

            {/* 👁️‍🗨️ THE X-RAY ENGINE (LIVE PERFORMANCE ANALYTICS CARD) */}
            <div className="clay-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#a855f7', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>👁️‍🗨️</span> The X-Ray Engine (Live Performance Analytics)
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '5px', lineHeight: 1.4, fontWeight: 'bold' }}>
                        Monitor homework folder attempts in real-time. Scan your students' brains, view coordinates/move discrepancies, and leave tips.
                    </p>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {/* Left: Homework List */}
                    <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto', paddingRight: '5px' }}>
                        {myHomeworks.length === 0 ? (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic', fontWeight: 'bold' }}>
                                You haven't assigned any homework yet. Use the Homework Launchpad above!
                            </p>
                        ) : (
                            myHomeworks.map(hw => {
                                const isSelected = selectedXrayHw?.id === hw.id;
                                return (
                                    <div 
                                        key={hw.id} 
                                        onClick={() => setSelectedXrayHw(hw)} 
                                        style={{ 
                                            background: isSelected ? '#a855f7' : 'rgba(255, 255, 255, 0.45)', 
                                            padding: '15px', 
                                            borderRadius: '16px', 
                                            cursor: 'pointer', 
                                            border: isSelected ? '1px solid #a855f7' : '2px solid rgba(255, 255, 255, 0.8)', 
                                            transition: 'all 0.2s ease', 
                                            boxShadow: isSelected ? '0 5px 15px rgba(168, 85, 247, 0.3)' : 'none',
                                            transform: isSelected ? 'scale(0.98)' : 'scale(1)'
                                        }}
                                    >
                                        <div style={{ color: isSelected ? 'white' : 'var(--text-primary)', fontWeight: '900', fontSize: '14px' }}>
                                            {hw.title}
                                        </div>
                                        <div style={{ color: isSelected ? '#f3e8ff' : 'var(--text-secondary)', fontSize: '11px', marginTop: '8px', display: 'flex', gap: '15px', fontWeight: 'bold' }}>
                                            <span>🎯 Assigned: {(hw.assignedTo || []).length}</span>
                                            <span>🏆 Solved: {(hw.completedBy || []).length}</span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Right: Homework Attempt Details */}
                    <div style={{ flex: '2 1 400px', background: 'rgba(255, 255, 255, 0.25)', padding: '20px', borderRadius: '20px', border: '2px solid rgba(255, 255, 255, 0.7)', maxHeight: '350px', overflowY: 'auto', boxShadow: 'var(--neu-shadow-inset)' }}>
                        {!selectedXrayHw ? (
                            <div style={{ height: '100%', minHeight: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#64748b', fontStyle: 'italic', fontSize: '13px', fontWeight: 'bold' }}>
                                👈 Select a homework folder from the list to scan students' brains.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <h4 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '15px', borderBottom: '1.5px solid rgba(163, 177, 198, 0.3)', paddingBottom: '10px', fontWeight: '900' }}>
                                    Scan Results: <span style={{ color: '#a855f7' }}>{selectedXrayHw.title}</span>
                                </h4>
                                {!(selectedXrayHw.activityLogs && selectedXrayHw.activityLogs.length > 0) ? (
                                    <p style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '15px', borderRadius: '16px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', margin: '15px 0', border: '1.5px solid rgba(16, 185, 129, 0.3)' }}>
                                        Awaiting first student attempt...
                                    </p>
                                ) : (
                                    Object.entries(
                                        (selectedXrayHw.activityLogs || []).reduce((acc, log) => {
                                            if(!acc[log.studentEmail]) acc[log.studentEmail] = { name: log.studentName, logs: [] };
                                            acc[log.studentEmail].logs.push(log);
                                            return acc;
                                        }, {})
                                    ).map(([email, data]) => {
                                        const safeEmail = email.replace(/\./g, '_');
                                        const savedComment = selectedXrayHw.coachComments?.[safeEmail] || '';
                                        return (
                                            <div key={email} style={{ background: 'rgba(255, 255, 255, 0.55)', padding: '15px', borderRadius: '16px', border: '2px solid rgba(255, 255, 255, 0.8)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                                <h5 style={{ margin: '0 0 10px 0', color: '#2563eb', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '900' }}>👤 {data.name}</h5>
                                                
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' }}>
                                                    {data.logs.map((l, i) => (
                                                        <div key={i} style={{ background: 'var(--bg-primary)', padding: '10px 12px', borderRadius: '12px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1.5px solid ${l.isCorrect ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
                                                            <div>
                                                                <div style={{ color: 'var(--text-primary)', fontWeight: '900', marginBottom: '3px' }}>Puzzle {l.taskIndex}: {l.taskDesc}</div>
                                                                <div style={{ color: 'var(--text-secondary)', fontWeight: 'bold' }}>Played: <span style={{ color: l.isCorrect ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>{l.playedMove}</span> {!l.isCorrect && ` (Expected: ${l.expectedMove})`}</div>
                                                            </div>
                                                            <div style={{ fontSize: '20px' }}>{l.isCorrect ? '✅' : '❌'}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input 
                                                        type="text" 
                                                        value={commentInput[email] !== undefined ? commentInput[email] : savedComment} 
                                                        onChange={e => setChecklist({...checklist, homework: true}) || setCommentInput({...commentInput, [email]: e.target.value})} 
                                                        placeholder={savedComment ? "Edit your sent feedback..." : "Write a personal tip or feedback..."} 
                                                        style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1.5px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }} 
                                                    />
                                                    <button 
                                                        onClick={() => handleSaveComment(selectedXrayHw.id, email)} 
                                                        className="clay-btn"
                                                        style={{ padding: '0 15px', background: '#3b82f6', color: 'white', fontWeight: '900', border: 'none', fontSize: '12px' }}
                                                    >
                                                        {savedComment ? '📝 Update' : '📩 Send Tip'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
        </div>
    );
}
